// components/StatusHistoryModal.js
import React from 'react';
import moment from 'moment';

const StatusHistoryModal = ({ 
  show, 
  onClose, 
  statusHistory, 
  currentStatus,
  academicYearName 
}) => {
  if (!show) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'inactive': return '⚪';
      case 'completed': return '✅';
      case 'archived': return '📁';
      default: return '⚪';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Status History</h2>
              <p className="text-gray-600 mt-2">
                Audit trail for {academicYearName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 text-gray-400 hover:text-gray-600 transition-colors rounded-2xl hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Current Status */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-blue-600 text-lg">{getStatusIcon(currentStatus)}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
                <p className="text-gray-600">
                  This academic year is currently <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(currentStatus)}`}>
                    {currentStatus}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Status History Timeline */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Status Change History</h3>
            
            {statusHistory && statusHistory.length > 0 ? (
              <div className="space-y-4">
                {statusHistory.map((history, index) => (
                  <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-white rounded-lg border border-gray-300 flex items-center justify-center">
                        <span className="text-gray-600 text-sm">
                          {getStatusIcon(history.fromStatus)}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-gray-500">From</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(history.fromStatus)}`}>
                          {history.fromStatus}
                        </span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        <span className="text-sm text-gray-500">To</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(history.toStatus)}`}>
                          {history.toStatus}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Changed by:</span>
                          <span className="ml-2 text-gray-600">{history.changedBy}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Date:</span>
                          <span className="ml-2 text-gray-600">
                            {moment(history.changedAt).format('MMM DD, YYYY hh:mm A')}
                          </span>
                        </div>
                        {history.reason && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">Reason:</span>
                            <span className="ml-2 text-gray-600">{history.reason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Status History</h3>
                <p className="text-gray-600">
                  No status changes have been recorded for this academic year yet.
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Close History
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusHistoryModal;